import React, { FC, RefObject, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormBaseType, FormState } from "@green0meter/api-client";

import FormBasicsDialog from "../CollectData/components/FormBasicsDialog";
import Co2Guide from "./Co2Guide";

import { useGetAvailableForms } from "@api";
import CardGuide from "@components/common/CardGuide/CardGuide";
import { useUserStore } from "@store/user";

interface Co2GuideContainer {
  refs?: {
    reportsRef?: RefObject<HTMLDivElement>;
    uploadsRef?: RefObject<HTMLDivElement>;
  };
}

const Co2GuideContainer: FC<Co2GuideContainer> = ({ refs }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { availableForms } = useGetAvailableForms(user, [FormBaseType.Co2]);

  const enabledForms = useMemo(
    () =>
      availableForms?.filter((form) => form.state === FormState.InProgress) ?? [],
    [availableForms],
  );

  const [newFormDialogOpen, setNewFormDialogOpen] = useState(false);

  const handleNewFormDialogClose = (): void => {
    setNewFormDialogOpen(false);
  };

  const steps: CardGuide["steps"] = [
    {
      title: t("common.stepNumber", { step: 1 }),
      description: t("co2.guide.steps.dataCollection"),
      detail: {
        title: t("co2.hub.guidanceTitle"),
        points: [
          t("co2.hub.assignQuestions"),
          t("co2.hub.respondentsResponsible"),
          t("co2.hub.ensureAccuracy"),
          t("co2.hub.attachDocuments"),
          t("co2.hub.submitButton"),
          t("co2.hub.waitConfirmation"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void => {
          if (enabledForms.length > 0) {
            const urlForFirstEnabledForm = `/co2/form/${enabledForms[0].id}`;

            return navigate(urlForFirstEnabledForm);
          }

          return setNewFormDialogOpen(true);
        },
      },
    },
    {
      title: t("common.stepNumber", { step: 2 }),
      description: t("co2.guide.steps.analysis"),
      detail: {
        title: t("co2.hub.guidanceTitle"),
        points: [
          t("co2hub.step2.p1"),
          t("co2hub.step2.p2"),
          t("co2hub.step2.p3"),
          t("co2hub.step2.p4"),
          t("co2hub.step2.p5"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void => navigate("/co2/dashboard"),
      },
    },
    {
      title: t("common.stepNumber", { step: 3 }),
      description: t("co2.guide.steps.reportGeneration"),
      detail: {
        title: t("co2.hub.guidanceTitle"),
        points: [
          t("co2hub.step3.p1"),
          t("co2hub.step3.p2"),
          t("co2hub.step3.p3"),
          t("co2hub.step3.p4"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void =>
          refs?.reportsRef?.current?.scrollIntoView({
            behavior: "smooth",
          }),
      },
    },
    {
      title: t("common.stepNumber", { step: 4 }),
      description: t("co2.guide.steps.audit"),
      detail: {
        title: t("co2.hub.guidanceTitle"),
        points: [
          t("co2hub.step4.p1"),
          t("co2hub.step4.p2"),
          t("co2hub.step4.p3"),
          t("co2hub.step4.p4"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void =>
          refs?.uploadsRef?.current?.scrollIntoView({
            behavior: "smooth",
          }),
      },
    },
    {
      title: t("common.stepNumber", { step: 5 }),
      description: t("co2.guide.steps.decarbonizationPlan"),
      detail: {
        title: t("co2.hub.guidanceTitle"),
        points: [
          t("co2hub.step5.p1"),
          t("co2hub.step5.p2"),
          t("co2hub.step5.p3"),
          t("co2hub.step5.p4"),
          t("co2hub.step5.p5"),
          t("co2hub.step5.p6"),
        ],
      },
      button: {
        text: t("common.openAction"),
        onClick: (): void =>
          navigate("/co2/dashboard?scrollTo=co2-recommendations-list"),
      },
    },
  ];

  return (
    <>
      <FormBasicsDialog
        open={newFormDialogOpen}
        formBaseType={FormBaseType.Co2}
        onClose={handleNewFormDialogClose}
      />
      <Co2Guide steps={steps} />
    </>
  );
};

export default Co2GuideContainer;
